import { useEffect, useState } from "react";
import { api, errMsg, fileUrl, API_BASE } from "../lib/api";
import { verifyLicenseText, verifyLicenseSignature } from "../lib/licenseCheck";
import { useAuth } from "../context/AuthContext";
import { Field, inputCls, selectCls, btnPrimary, btnSecondary } from "../components/Modal";
import { toast } from "sonner";

const ALL_METHODS = ["Espèces", "Mobile Money", "Orange Money", "Moov Money", "Wave", "Banque", "Chèque"];
const PREFIX_FIELDS = [
  ["VTE", "Ventes / Factures"], ["DEV", "Devis"], ["PRO", "Factures pro forma"],
  ["BL", "Bons de livraison"], ["ACH", "Achats"], ["INV", "Inventaires"],
];

export default function SettingsPage() {
  const { reload } = useAuth();
  const [form, setForm] = useState(null);
  const [license, setLicense] = useState(null);
  const [licText, setLicText] = useState("");
  const [backups, setBackups] = useState([]);

  const loadLicense = () => api.get("/license").then((r) => setLicense(r.data)).catch(() => {});
  const loadBackups = () => api.get("/system/backups").then((r) => setBackups(r.data.backups || [])).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch((e) => toast.error(errMsg(e)));
    loadLicense();
  }, []);
  useEffect(() => { if (license?.app_mode === "LOCAL") loadBackups(); }, [license]);

  const backupNow = async () => {
    try {
      const r = await api.post("/system/backup");
      toast.success(`Sauvegarde créée : ${r.data.name}`);
      loadBackups();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importLicense = async () => {
    // Vérification cryptographique locale (hors-ligne) avant tout appel serveur
    const check = verifyLicenseText(licText, license?.installation_id);
    if (!check.ok) { toast.error(check.error); return; }
    try {
      const { data } = await api.post("/license/import", { text: licText });
      toast.success(`Licence activée — valide jusqu'au ${data.expires_at}`);
      setLicText("");
      loadLicense();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!form) return <div className="py-20 text-center text-muted-foreground">Chargement...</div>;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const prefixes = form.number_prefixes || {};
  const methods = form.payment_methods?.length ? form.payment_methods : ALL_METHODS;

  const toggleMethod = (m) => {
    const cur = methods.includes(m) ? methods.filter((x) => x !== m) : [...methods, m];
    setForm({ ...form, payment_methods: cur });
  };

  const pickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/settings/logo", fd);
      setForm({ ...form, logo_path: data.logo_path });
      toast.success("Logo mis à jour");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/settings/logo");
      setForm({ ...form, logo_path: "" });
      toast.success("Logo supprimé");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const save = async () => {
    try {
      await api.patch("/settings", {
        name: form.name, phone: form.phone, whatsapp: form.whatsapp, address: form.address,
        city: form.city, country: form.country, currency: form.currency, logo_url: form.logo_url,
        tax_rate: Number(form.tax_rate) || 0,
        default_alert_threshold: Number(form.default_alert_threshold) || 0,
        payment_methods: methods,
        print_format: form.print_format || "A4",
        pos_barcode_autofocus: !!form.pos_barcode_autofocus,
        number_prefixes: prefixes,
      });
      toast.success("Paramètres enregistrés");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const Section = ({ title, children }) => (
    <div className="bg-card border rounded-xl p-5 space-y-4">
      <h2 className="font-heading font-bold text-lg">{title}</h2>
      {children}
    </div>
  );

  return (
    <div className="space-y-4 max-w-3xl" data-testid="settings-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Paramètres de la quincaillerie</h1>

      <Section title="Informations">
        <Field label="Nom de la quincaillerie"><input data-testid="set-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Téléphone"><input data-testid="set-phone" value={form.phone || ""} onChange={set("phone")} className={inputCls} /></Field>
          <Field label="WhatsApp"><input data-testid="set-whatsapp" value={form.whatsapp || ""} onChange={set("whatsapp")} className={inputCls} /></Field>
          <Field label="Ville"><input data-testid="set-city" value={form.city || ""} onChange={set("city")} className={inputCls} /></Field>
          <Field label="Pays"><input data-testid="set-country" value={form.country || ""} onChange={set("country")} className={inputCls} /></Field>
        </div>
        <Field label="Adresse"><input data-testid="set-address" value={form.address || ""} onChange={set("address")} className={inputCls} /></Field>
        <Field label="Logo de la quincaillerie">
          <div className="flex items-center gap-3 flex-wrap">
            {form.logo_path && <img data-testid="set-logo-preview" src={fileUrl(form.logo_path)} alt="logo" className="h-14 w-14 rounded-lg object-contain border bg-white p-1" />}
            <input data-testid="set-logo-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={pickLogo} className="text-sm" />
            {form.logo_path && <button type="button" data-testid="set-logo-remove" onClick={removeLogo} className="text-xs text-rose-600 font-semibold hover:underline">Retirer</button>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP ou GIF — 5 Mo max. Affiché dans la barre latérale et sur les factures PDF.</p>
        </Field>
      </Section>

      <Section title="Devise & taxes">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Devise">
            <select data-testid="set-currency" value={form.currency} onChange={set("currency")} className={selectCls}>
              <option value="FCFA">FCFA (XOF)</option>
              <option value="GNF">GNF</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
          </Field>
          <Field label="Taux de taxe (%)"><input data-testid="set-tax-rate" type="number" min="0" step="0.1" value={form.tax_rate || 0} onChange={set("tax_rate")} className={inputCls} /></Field>
        </div>
      </Section>

      <Section title="Numérotation des documents">
        <p className="text-xs text-muted-foreground">Format : PREFIXE-ANNÉE-00001 (ex : FAC-2026-00001)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PREFIX_FIELDS.map(([k, label]) => (
            <Field key={k} label={label}>
              <input data-testid={`set-prefix-${k}`} value={prefixes[k] || k}
                onChange={(e) => setForm({ ...form, number_prefixes: { ...prefixes, [k]: e.target.value.toUpperCase() } })}
                className={inputCls} />
            </Field>
          ))}
        </div>
      </Section>

      <Section title="Stock">
        <Field label="Seuil d'alerte par défaut (nouveaux produits)">
          <input data-testid="set-threshold" type="number" min="0" value={form.default_alert_threshold || 0} onChange={set("default_alert_threshold")} className={inputCls} />
        </Field>
      </Section>

      <Section title="Caisse & Point de Vente">
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Modes de paiement acceptés</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="set-payment-methods">
            {ALL_METHODS.map((m) => (
              <label key={m} className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" data-testid={`set-method-${m.toLowerCase().replace(/\s/g, "-")}`}
                  checked={methods.includes(m)} onChange={() => toggleMethod(m)} className="h-4 w-4 accent-primary" />
                {m}
              </label>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Format d'impression par défaut">
            <select data-testid="set-print-format" value={form.print_format || "A4"} onChange={set("print_format")} className={selectCls}>
              <option value="A4">Facture A4</option>
              <option value="80mm">Ticket 80 mm</option>
            </select>
          </Field>
          <Field label="Scan code-barres auto au POS">
            <label className="flex items-center gap-2 h-10 text-sm font-medium">
              <input data-testid="set-barcode-autofocus" type="checkbox" checked={!!form.pos_barcode_autofocus}
                onChange={(e) => setForm({ ...form, pos_barcode_autofocus: e.target.checked })} className="h-4 w-4 accent-primary" />
              Champ de scan toujours actif
            </label>
          </Field>
        </div>
        <p className="text-xs text-muted-foreground">Les deux formats restent disponibles après chaque vente (Facture A4 / Ticket 80 mm).</p>
      </Section>

      <Section title="Licence — Édition Locale">
        {!license ? <p className="text-sm text-muted-foreground">Chargement...</p> : (
          <div className="space-y-3" data-testid="license-section">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="text-muted-foreground">Identifiant installation :</span>
              <code data-testid="install-id" className="font-mono font-bold bg-muted px-2 py-1 rounded">{license.installation_id}</code>
              <button data-testid="copy-install-id" onClick={() => { navigator.clipboard.writeText(license.installation_id); toast.success("Identifiant copié"); }} className={btnSecondary}>Copier</button>
            </div>
            {license.license ? (
              license.status.status === "ACTIVE" ? (
                <div data-testid="license-status" className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950 p-3 text-sm text-emerald-800 dark:text-emerald-200 font-semibold">
                  Licence valide — {license.license.hardware_store_name} · expire le {license.license.expires_at} ({license.status.days_left} jour(s) restants)
                </div>
              ) : (
                <div data-testid="license-status" className="rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-950 p-3 text-sm text-rose-800 dark:text-rose-200 font-semibold">
                  {license.status.status === "EXPIRED" ? "LICENCE EXPIRÉE — renouvelez-la pour continuer à effectuer de nouvelles opérations." : `Licence ${license.status.status}`}
                </div>
              )
            ) : (
              <div data-testid="license-status" className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950 p-3 text-sm text-amber-800 dark:text-amber-200 font-semibold">
                APPLICATION NON ACTIVÉE — exportez la demande d'activation et transmettez-la au Super Administrateur.
              </div>
            )}
            {license.license && (() => {
              const { signature, ...payload } = license.license;
              return verifyLicenseSignature(payload, signature)
                ? <p className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold" data-testid="license-local-verify">✓ Signature vérifiée localement (hors-ligne)</p>
                : <p className="text-xs text-rose-700 dark:text-rose-400 font-semibold" data-testid="license-local-verify">✗ Signature invalide ou licence modifiée</p>;
            })()}
            <div>
              <button data-testid="export-qreq" onClick={() => window.open(`${API_BASE}/license/activation-request`, "_blank")} className={btnSecondary}>Exporter la demande d'activation (.qreq)</button>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Activer / Renouveler</div>
              <input data-testid="license-file" type="file" accept=".qlic,application/json" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setLicText(await f.text()); }} className="text-sm" />
              <textarea data-testid="license-code" value={licText} onChange={(e) => setLicText(e.target.value)} placeholder="Ou collez le code d'activation QLIC1.…" className={`${inputCls} h-20 py-2 font-mono text-xs`} />
              <button data-testid="license-import-btn" onClick={importLicense} disabled={!licText.trim()} className={btnPrimary}>Importer la licence</button>
            </div>
          </div>
        )}
      </Section>

      {license?.app_mode === "LOCAL" && (
        <Section title="Sauvegardes locales">
          <p className="text-xs text-muted-foreground">Automatiques : 1 par jour (rétention : 7 quotidiennes, 4 hebdomadaires, 12 mensuelles). Copiez régulièrement un fichier sur clé USB.</p>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="backup-now" onClick={backupNow} className={btnPrimary}>Sauvegarder maintenant</button>
            <button data-testid="sync-export" onClick={() => window.open(`${API_BASE}/sync/export`, "_blank")} className={btnSecondary}>Exporter une sauvegarde complète (JSON)</button>
            <button data-testid="sync-export-incr" onClick={() => window.open(`${API_BASE}/sync/export?since=auto`, "_blank")} className={btnSecondary}>Exporter les nouveautés (depuis la dernière sync)</button>
          </div>
          {backups.length > 0 && (
            <div className="border rounded-lg divide-y" data-testid="backup-list">
              {backups.map((b) => (
                <div key={b.name} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="font-mono">{b.name}</span>
                  <span className="text-xs text-muted-foreground">{(b.size / 1024).toFixed(0)} Ko</span>
                  <span className="flex-1" />
                  <button data-testid={`backup-dl-${b.name}`} onClick={() => window.open(`${API_BASE}/system/backups/${b.name}/download`, "_blank")} className="text-xs font-bold text-primary hover:underline">Télécharger</button>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      <button data-testid="save-settings" onClick={save} className={btnPrimary}>Enregistrer tous les paramètres</button>
    </div>
  );
}
